import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast, useAuth } from "@/comn/hooks";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    URLS,
    SF_DOC_RCPN_MGMT_SRCH,
    SG_DOC_RCPN_MGMT_LIST,
} from "@/tra/tancis/ptli/elctdoc/rcpn/services/PtliDocRcpnMgmtService";

/*
 * Electronic Document Reception Management
 * !== 전자문서 수신 관리 ==!
 */
export const PTLI0701001Q = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0047", "auth_ptli0004", "auth_ptli0049", "auth_ptli0015", "auth_ptlianyADM", "auth_ptli0048"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0068" },
            { area: "comnCd", comnCd: "COM0070" },
            { area: "comnCd", comnCd: "COM0077" },
            { area: "comnCd", comnCd: "COM0024" }, // 업무 분류 코드
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docRcpnMgmtSrch: useForm({
            defaultSchema: SF_DOC_RCPN_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
                startFrstRgsrDtm: pgeStore?.form?.startFrstRgsrDtm || comnUtils.getDate(),
                endFrstRgsrDtm: pgeStore?.form?.endFrstRgsrDtm || comnUtils.getDate(),
            },
        }),
    };

    const grid = {
        docRcpnMgmtList: useGrid({
            defaultSchema: SG_DOC_RCPN_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get a list of Receive electronic documents. !== 전자문서 수신 목록을 조회한다. ==!
        getElctDocRcpnList: useFetch({
            api: (page = grid.docRcpnMgmtList.page) =>
                APIS.getElctDocRcpnList(form.docRcpnMgmtSrch.getValues(), page, grid.docRcpnMgmtList.size),
            enabled: comnUtils.isEmpty(form.docRcpnMgmtSrch.errors) && form.docRcpnMgmtSrch.isSubmitted,
            key: [grid.docRcpnMgmtList.page, grid.docRcpnMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.docRcpnMgmtSrch.getValues(),
                    page: grid.docRcpnMgmtList.page,
                    size: grid.docRcpnMgmtList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        getElctDocRcpnList: (page: number = 0) => {
            form.docRcpnMgmtSrch.handleSubmit(
                () => {
                    grid.docRcpnMgmtList.setPage(page);
                    fetch.getElctDocRcpnList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Enter Keyword text !== 검색어 엔터 이벤트 ==!
        onEnterKwdTxt: () => {
            grid.docRcpnMgmtList.setPage(0);
            fetch.getElctDocRcpnList.fetch(0);
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_docRcpnMgmtList: {
            cell: {
                trscLogId: (props: any) => {
                    const { rowValues, value } = props;
                    return <Link to={`/ptli/elctdoc/rcpn/ptli0701002q/${rowValues.trscLogId}`}>{value}</Link>;
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    const kwdSel = form.docRcpnMgmtSrch.watch("kwdSel");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.docRcpnMgmtSrch.schema.bsopClsfCd}></Group.Control>
                            <Group.Control {...form.docRcpnMgmtSrch.schema.startTimeRnge}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docRcpnMgmtSrch.schema.sndrcvTpCd}></Group.Control>
                            <Group.Control {...form.docRcpnMgmtSrch.schema.sndrcvDvnCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docRcpnMgmtSrch.schema.intfSttsCd}></Group.Control>
                            <Group.Label label={"L_KWD"} />
                            <Group.Col padding={1}>
                                <Layout direction="row" gap={1}>
                                    <FormControl {...form.docRcpnMgmtSrch.schema.kwdSel} />
                                    <FormControl
                                        {...form.docRcpnMgmtSrch.schema.kwdTxt}
                                        readOnly={!kwdSel}
                                        onEnter={handler.onEnterKwdTxt}
                                    />
                                </Layout>
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.docRcpnMgmtSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getElctDocRcpnList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.docRcpnMgmtList.grid}
                            data={fetch.getElctDocRcpnList.data?.elctDocRcpnList}
                            render={render.grid_docRcpnMgmtList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
